import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Main.css";
import useOnePageScroll from "../hooks/useOnePageScroll";
import heroBg from "../assets/hero-bg.jpg";
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaYoutube } from "react-icons/fa"; 
import { SiNaver } from "react-icons/si";
import { Helmet } from 'react-helmet-async';

const API_BASE = process.env.REACT_APP_API_BASE || "";

export default function Main() {
  const { t, i18n } = useTranslation();
  const [latest, setLatest] = useState({ filmography: [], gallery: [], notice: [] });
  const [galleryWithMeta, setGalleryWithMeta] = useState([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  const getLangData = (item, field) => {
    const lang = (i18n.language || 'KR').toLowerCase();
    const key = `${field}_${lang}`;
    return item[key] || item[`${field}_kr`] || item[field] || "";
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useOnePageScroll({
    selector: ".home-section",
    lockMs: 750,
    threshold: 12,
    enabled: true,
    containerSelector: ".page-content",
  });

  useEffect(() => {
    document.body.classList.add('is-home-active');
    return () => {
      document.body.classList.remove('is-home-active');
    };
  }, []);

  useEffect(() => {
    const getInitialCount = () => {
      const width = window.innerWidth;
      if (width <= 480) return 4;  // 모바일은 4개
      if (width <= 1024) return 6; // 태블릿은 6개
      return 8;                    // PC는 8개
    }

    const galCount = getInitialCount();

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/home_latest.php?filmN=10&galN=${galCount}&noticeN=5`, {
          credentials: "include",
        });
        const data = await res.json();
        
        setLatest({
          filmography: data.filmography || [],
          gallery: data.gallery || [],
          notice: data.notice || [],
        });

        if (data.gallery) {
          const metaPromises = data.gallery.map((it) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.src = `${API_BASE}${it.image_path}`;
              img.onload = () => {
                resolve({ ...it, isPortrait: img.height > img.width });
              };
              img.onerror = () => resolve({ ...it, isPortrait: false });
            });
          });
          const results = await Promise.all(metaPromises);
          setGalleryWithMeta(results);
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  const filmDrama = latest.filmography.filter((x) => (x.category || "").toUpperCase() === "DRAMA").slice(0, 3);
  const filmFeature = latest.filmography.filter((x) => (x.category || "").toUpperCase() === "FEATURE FILM").slice(0, 3);

  return (
    <>
    <Helmet>
      <title>배우 한지우 홈페이지</title>
      <meta name="description" content="배우 한지우의 홈페이지입니다. 최신 소식, 필모그래피, 포트폴리오 및 갤러리를 확인하세요." />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="배우 한지우 홈페이지" />
      <meta property="og:description" content="배우 한지우의 홈페이지입니다. 최신 소식, 필모그래피, 포트폴리오 및 갤러리를 확인하세요." />
      <meta property="og:url" content="https://hanjiwoo.com/" />
      <meta property="og:image" content="https://hanjiwoo.com/og/og-home.jpg" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="배우 한지우 홈페이지" />
      <meta name="twitter:description" content="배우 한지우의 홈페이지입니다. 최신 소식, 필모그래피, 포트폴리오 및 갤러리를 확인하세요." />
      <meta name="twitter:image" content="https://hanjiwoo.com/og/og-home.jpg" />

      <link rel="canonical" href="https://hanjiwoo.com/" />
    </Helmet>
    <main className="home">
      {/* 1. HERO SECTION */}
      <section className="home-section hero" id="home" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-inner">
          <div className="hero-text-group">
            <p className="hero-kicker">ACTOR</p>
            <h1 className="hero-title">HAN JIWOO</h1>
            {/* <p className="hero-sub">Sophisticated Performance · Versatile Soul</p> */}
          </div>
        </div>
        <div className="scroll-hint"><span /></div>
      </section>

      {/* 2. ABOUT SECTION */}
      {/* <section className="home-section" id="about">
        <div className="section-head">
          <h2>ABOUT</h2>
          <Link className="more-link" to="/contact">{t('more')}</Link>
        </div>
        <div className="section-body about-preview-card">
          <div className="about-text-content">
            <p>{t('about_text')}</p>
          </div>
        </div>
      </section> */}

      {/* 3. FILMOGRAPHY SECTION */}
      <section className="home-section" id="filmography">
        <div className="section-head">
          <h2 className="cinema-title">{t('nav_filmo')}</h2>
          <Link className="more-link" to="/filmography">{t('more')}</Link>
        </div>
        
        <div className="section-body film-layout-grid">
          {/* DRAMA COLUMN */}
          <div className="film-column">
            <div className="category-header">
              <span className="line"></span>
              <h3 className="category-name">DRAMA</h3>
              <span className="line"></span>
            </div>
            
            <div className="film-stack">
              {filmDrama.length === 0 ? <p className="muted">{t('no_data')}</p> : 
                filmDrama.map(x => (
                  <div key={x.id} className="film-entry">
                    <div className="entry-year">{x.year}</div>
                    <div className="entry-details">
                      <strong className="entry-title">{getLangData(x, 'title')}</strong>
                      <span className="entry-role">{getLangData(x, 'role')}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* MOVIE COLUMN */}
          <div className="film-column">
            <div className="category-header">
              <span className="line"></span>
              <h3 className="category-name">MOVIE</h3>
              <span className="line"></span>
            </div>

            <div className="film-stack">
              {filmFeature.length === 0 ? <p className="muted">{t('no_data')}</p> : 
                filmFeature.map(x => (
                  <div key={x.id} className="film-entry">
                    <div className="entry-year">{x.year}</div>
                    <div className="entry-details">
                      <strong className="entry-title">{getLangData(x, 'title')}</strong>
                      <span className="entry-role">{getLangData(x, 'role')}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </section>

      {/* 4. GALLERY SECTION */}
      <section className="home-section" id="gallery">
        <div className="section-head">
          <h2>{t('nav_gallery')}</h2>
          <Link className="more-link" to="/gallery">{t('more')}</Link>
        </div>
        <div className="section-body masonry-grid">
          {galleryWithMeta.map((it) => (
            <div 
              key={it.id} 
              className={`masonry-item ${it.isPortrait ? 'portrait' : 'landscape'}`}
            >
              <Link to="/gallery">
                <img src={`${API_BASE}${it.image_path}`} alt={it.title || "배우 한지우 이미지"} loading="lazy" />
                <div className="item-overlay"><span>{it.title}</span></div>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* 5. NOTICE SECTION */}
      <section className="home-section" id="notice">
        <div className="section-head">
          <h2>{t('nav_notice')}</h2>
          <Link className="more-link" to="/notice">{t('more')}</Link>
        </div>
        <div className="section-body notice-modern-list">
          {latest.notice.length === 0 ? (
            <p className="muted">{t('no_data')}</p>
          ) : (
            latest.notice.map((n) => (
              <Link to="/notice" className="notice-row" key={n.id}>
                <span className="n-date">{n.date.replace(/-/g, '.')}</span>
                <span className="n-title">{getLangData(n, 'title')}</span>
                <span className="n-arrow">→</span>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* 6. CONTACT SECTION */}
      <section className="home-section" id="contact">
        <div className="section-head">
          <h2>{t('nav_info')}</h2>
        </div>
        <div className="section-body contact-container">
          <div className="contact-info-card">
            <div className="info-grid">
              <div className="info-item">
                <label>{t('info_phy')}</label>
                <p>166cm / 47kg</p>
              </div>
              <div className="info-item">
                <label>{t('info_skill')}</label>
                <p>{t('info_action')}<br/>{t('info_running')}<br/>{t('info_boxing')}</p>
              </div>
            </div>

            <div className="social-links">
              <a href="https://instagram.com/z.wo_o" target="_blank" rel="noreferrer" className="social-btn">
                <FaInstagram size={20} /> <span className="btn-text">Instagram</span>
              </a>
              <a href="https://www.youtube.com/@naneunjiwoo" target="_blank" rel="noreferrer" className="social-btn">
                <FaYoutube size={22} /> <span className="btn-text">YouTube</span>
              </a>
              <a href="https://cafe.naver.com/creditjiwoo" target="_blank" rel="noreferrer" className="social-btn">
                <SiNaver size={16} /> <span className="btn-text">Fan Cafe</span>
              </a>
            </div>
          </div>
        </div>
        {/* <div className="footer">
          <p>© 2026 Actor Han Jiwoo. All rights reserved.</p>
        </div> */}
      </section>
    </main>
    </>
  );
}